import React, { useEffect, useMemo, useState } from 'react';
import { HbrBadge, HbrCheckbox, HbrIcon, HbrOption } from '@harbor/elements-react-standalone';
import { caretDown } from '@harbor/elements/icons';

export interface StbMultiSelectGroupItem {
  id: number | string;
  name: string;
  [key: string]: any;
}

interface StbMultiSelectCollapsibleProps {
  label: string;
  items: StbMultiSelectGroupItem[];
  selectedItems?: StbMultiSelectGroupItem[];
  onChange?: (selected: StbMultiSelectGroupItem[]) => void;
  open?: boolean;
}

const StbMultiSelectCollapsible: React.FC<StbMultiSelectCollapsibleProps> = ({
                                                                               label,
                                                                               items,
                                                                               selectedItems = [],
                                                                               onChange = () => {},
                                                                               open = false,
                                                                             }) => {
  const [showBody, setShowBody] = useState<boolean>(open);

  const options: { item: StbMultiSelectGroupItem; checked: boolean }[] = useMemo(() => {
    return items.map((item) => ({
      item,
      checked: selectedItems.some((selected) => selected.id === item.id),
    }));
  }, [selectedItems, items]);

  useEffect(() => {
    setShowBody(open);
  }, [open]);

  const handleCheckClick = (item: StbMultiSelectGroupItem): void => {
    const newOptions = options.map((option) =>
      option.item.id === item.id ? { ...option, checked: !option.checked } : option,
    );

    onChange(newOptions.filter((option) => option.checked).map((option) => option.item));
  };

  return (
    <div className="w-full min-w-0">
      <header
        className="flex justify-between items-center w-full min-w-0 px-4 py-2 cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          setShowBody((prev) => !prev);
        }}
      >
        <div className="flex items-center flex-row gap-x-4 min-w-0 flex-1">
          <h4 className="min-w-0 truncate">{label}</h4>
          {selectedItems.length > 0 && (
            <HbrBadge pill size="small" sentiment="info">
              {selectedItems.length}
            </HbrBadge>
          )}
        </div>

        <HbrIcon
          asset={caretDown}
          className={`duration-300 transition-all ml-2 shrink-0 ${showBody ? 'rotate-180' : ''}`}
        />
      </header>

      {showBody && (
        <div className="w-full min-w-0 overflow-x-hidden pb-2">
          <div className="min-w-0">
            {options.map((option) => (
              <HbrOption key={option.item.id} className="block w-full min-w-0" size="small">
                <div className="w-full min-w-0 px-4">
                  <HbrCheckbox
                    className="block w-full min-w-0"
                    checked={option.checked}
                    onHbr-click={() => {
                      handleCheckClick(option.item);
                    }}
                  >
                    <span className="block min-w-0 truncate" title={option.item.name}>
                      {option.item.name}
                    </span>
                  </HbrCheckbox>
                </div>
              </HbrOption>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StbMultiSelectCollapsible;